import { Injectable } from '@angular/core';
import type { GridStackOptions } from 'gridstack';

import { JeptoGridStackEngine } from '../utils/jepto-grid-stack-engine';

@Injectable({
	providedIn: 'root',
})
export class GridStackConfigService {
	readonly defaultOptions: GridStackOptions = {
		// Fixed pixel row height, intentionally NOT 'auto'/square: column width is
		// fluid (scales with viewport + columnOpts breakpoints) but card height must
		// stay stable so chart/table content keeps a usable, predictable size. Tying
		// row height to column width made cards balloon/shrink — and jump non-
		// monotonically at breakpoints, since fewer columns can mean *wider* columns.
		cellHeight: 10,
		column: 24,
		// Float mode so cards stay where they are dropped and can sit lower than
		// their neighbours (JEP-10769). Gravity mode compacted every card upward.
		// Plain float mode pushes the card you drag onto further down, so a card
		// could not be dropped under the last row (JEP-10485);
		// JeptoGridStackEngine skips past the covered card instead.
		float: true,
		engineClass: JeptoGridStackEngine,
		animate: true,
		margin: 12,
		minRow: 1,
		draggable: {
			handle: '.grid-stack-item-content',
			appendTo: 'body',
			// Do not set helper:'clone': cloneNode cannot copy Highcharts canvases,
			// and the clone is appended to body so .grid-stack card chrome never
			// applies — empty/title-only drag preview (JEP-10485).
			scroll: true,
		},
		resizable: {
			handles: 'se',
		},
		// Single mobile breakpoint only — intentionally NOT a graduated series
		// (e.g. 24 -> 12 -> 4 cols). Column width is fluid and rescales continuously
		// with grid width already; card height is fixed pixels (see cellHeight
		// above). Any *intermediate* column-count drop makes the remaining columns
		// proportionally wider while height stays put, distorting a manually-tuned
		// card's aspect ratio at each step (can even flip landscape -> portrait).
		// Below 480px we deliberately want that: force full-width single-column
		// stacking (GridStack's built-in mobile behavior), same intent as the old
		// custom CSS-flex mobile bypass this replaced.
		columnOpts: {
			// Compare viewport width, not the grid container's own width (GridStack
			// default). Container width depends on the surrounding app shell (sidebar,
			// padding) and can stay > 480px on a narrow phone, so the container-based
			// check silently never fires and cards never stack to full width.
			breakpointForWindow: true,
			// Required: GridStack defaults columnMax to 12 when columnOpts is set
			// (`resp.columnMax = resp.columnMax || 12`), then checkDynamicColumn()
			// uses that as the desktop column count. Without an explicit 24, the
			// live grid clamps to 12 and AI 24-col layouts (e.g. x:12,w:12) fail
			// to apply in place until remount/reload.
			columnMax: 24,
			breakpoints: [{ w: 480, c: 1 }],
		},
		itemClass: 'grid-stack-item-content-wrapper',
	};

	getOptions(overrides?: Partial<GridStackOptions>): GridStackOptions {
		return { ...this.defaultOptions, ...overrides };
	}
}
